<template>
    <ValidationObserver tag="form" ref="observer" class="tw:space-y-6" @submit.prevent="submit">
        <BaseInputText
            data-cy="nombre"
            name="nombre"
            label="Nombre"
            rules="required"
            :value="local.nombre"
            @input="val => local.nombre = val"
        />

        <BaseMultiselect
            data-cy="representante"
            name="representante"
            label="Representante"
            :options="representantesLista"
            label-key="name"
            value-key="id"
            placeholder=" "
            :loading="isLoadingRepresentantes"
            :value="local.representante"
            @input="val => local.representante = val"
        />
        <p v-if="showNoRepresentantes" class="tw:text-sm tw:text-gray-400">No hay representantes disponibles</p>

        <div class="tw:space-y-3">
            <label class="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wider tw:text-gray-500">
                Imagen del equipo
                <span class="tw:block tw:font-normal tw:normal-case tw:text-gray-400 tw:text-xs tw:mt-1">
                    MEDIDAS: 1020x340 — FORMATO: JPG — PESO MÁXIMO: Menos de 500 KB
                </span>
            </label>

            <div v-if="local.imagen" class="tw:text-center tw:space-y-3">
                <img :src="local.imagen_formatted" class="tw:max-h-48 tw:mx-auto tw:rounded" />
                <Button @click="imagenBorrada">Borrar imagen</Button>
            </div>

            <Dropzone
                v-else
                :url="uploadUrl"
                :height="340"
                :width="1020"
                @success="imagenSubida"
            />
        </div>

        <div class="tw:flex tw:justify-end tw:pt-4">
            <Button type="submit" :disabled="saving">
                Guardar
            </Button>
        </div>
    </ValidationObserver>
</template>

<script setup>
    import { ref, reactive, onMounted, computed } from 'vue';
    import store from '@/store';
    import { config } from '@/config.js';
    import { useToast } from '@/composables/common/useToast';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import { Dropzone } from '@/modules/ui';

    const props = defineProps({
        equipo: {
            type: Object,
            default: () => ({})
        },
        listaId: {
            type: [String, Number],
            required: true
        },
        saving: {
            type: Boolean,
            default: false
        },
        representantes: {
            type: Array,
            default: null
        },
        representantesLoading: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['terminado']);
    const toast = useToast();

    const representantesLoadingLocal = ref(false);
    const representantesLocales = ref([]);
    const imagenUploaded = ref(null);
    const observer = ref(null);

    // Usa la prop si se pasa, sino carga internamente
    const representantesLista = computed(() =>
        props.representantes !== null ? props.representantes : representantesLocales.value
    );

    // Combinar la prop del padre con el estado local
    const isLoadingRepresentantes = computed(() =>
        props.representantes !== null ? props.representantesLoading : representantesLoadingLocal.value
    );

    const showNoRepresentantes = computed(() =>
        !isLoadingRepresentantes.value && representantesLista.value.length === 0
    );

    // Copia local para no mutar la prop
    // BaseMultiselect espera ID (String/Number), no el objeto completo
    const local = reactive({
        ...props.equipo,
        representante: props.equipo.representante?.id ?? props.equipo.representante ?? ''
    });

    const token = localStorage.getItem('token');
    const uploadUrl = computed(() =>
        config.API_BASE + '/upload/image-equipo?api_key=' + token + '&permisos=public'
    );

    function imagenBorrada() {
        local.imagen = '';
    }

    function imagenSubida(file, response) {
        imagenUploaded.value = response;
    }

    async function submit() {
        if (props.saving) return;
        const isValid = await observer.value?.validate();
        if (!isValid) return;

        if (imagenUploaded.value) {
            local.imagen = imagenUploaded.value;
        }

        const payload = { ...local };

        // BaseMultiselect emite solo el ID, pero la API espera { id, name }
        if (payload.representante) {
            const found = representantesLista.value.find(r =>
                (typeof r === 'object' ? r.id : r) === payload.representante
            );
            if (found) {
                payload.representante = found;
            }
        } else {
            delete payload.representante;
        }

        emit('terminado', payload);
    }

    async function getRepresentantes() {
        const params = {
            lista_id: props.listaId,
            filters: {}
        };
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.get.representantes'),
                params
            );
            representantesLocales.value = res.data.representantes || [];
        } catch (error) {
            console.error('Error loading representantes:', error);
            toast.error('Error al cargar los representantes');
        } finally {
            representantesLoadingLocal.value = false;
        }
    }

    onMounted(() => {
        if (props.representantes !== null) {
            // Ya vienen del padre, no hace falta cargarlos
            representantesLoadingLocal.value = false;
        } else {
            getRepresentantes();
        }
    });
</script>
